import { FC, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";
import { useCardBalance } from "@/hooks/use-queries";
import { useCardStyle } from "@/hooks/use-card-style";

type CardStripProps = {
  card: Card;
  className?: string;
};

// Compact, full-width card used inside sheets (same art and border as the
// card list in Top Up to Card), with the balance on top.
const CardStrip: FC<CardStripProps> = ({ card, className = "" }) => {
  const formatAmount = useFormatAmountWithCurrency();
  const { balance } = useCardBalance(card);
  const [showAmount, setShowAmount] = useState(true);
  const getCardStyle = useCardStyle();
  const style = getCardStyle(card?.bin, card?.network, card?.provider);
  const isVisa = card?.network === "VISA" || card?.network === "Visa";

  return (
    <div
      className={`font-dm-sans relative w-full overflow-hidden rounded-xl border border-[#CECECE2E] p-4 text-white shadow-[rgba(0,0,0,0.25)_0px_25px_50px_-12px] ${className}`}
      style={{ background: style.background }}
    >
      <div className="relative z-10">
        <div className="flex items-center gap-2 font-semibold">
          <span>{showAmount ? formatAmount(balance?.available) : "****"}</span>
          <Button
            variant="ghost"
            onClick={() => setShowAmount(!showAmount)}
            className="size-auto cursor-pointer !p-1 hover:bg-white/10"
          >
            {showAmount ? (
              <EyeOff className="size-4 text-white/40" />
            ) : (
              <Eye className="size-4 text-white/40" />
            )}
          </Button>
        </div>

        <div className="mt-4 flex items-end justify-between">
          <div>
            <p className="text-sm font-medium">
              **** **** **** <span className="text-xs">{card?.last4}</span>
            </p>
            <p className="mt-1 text-[10px] font-medium capitalize">
              {card?.firstName} {card?.lastName}
            </p>
          </div>
          {isVisa ? (
            <img className="w-10" src="/images/visa-white.png" alt="Visa" />
          ) : (
            <img
              className="w-10"
              src="/images/mastercard-logo.svg"
              alt="Mastercard"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CardStrip;
